import { useQuestionsStore } from '../stores/QuestionsStore'
import { storeToRefs } from 'pinia'
import { RequestService } from '../services/RequestService'
import { useEmployerStore } from '../stores/EmployerStore'
import { get } from 'lodash'
import { inject } from 'vue'
import * as Sentry from '@sentry/vue'
import {
  normalizeAnswer,
  buildAnswersMap,
  showQuestionCore,
  hideDueICHRACore,
  isAssessmentCompleteCore,
  isPlanCompleteCore
} from '../utils/questionsAndAnswersHelpers'

const { put, handleErrors } = RequestService()

// Re-export helpers for backward compatibility with existing imports
export { normalizeAnswer, buildAnswersMap }

/**
 * Pure version of showQuestion - wrapper around showQuestionCore for answersMap usage.
 * @param {Object} question - The question object with hideLogic
 * @param {Object} answersMap - Map of questionId -> answer value
 * @returns {Object} - { show: boolean, hideReason: string, questionHideReason: any, reasons: array, questionsHideReasons: array }
 */
export function showQuestionPure(question, answersMap) {
  const getAnswer = (id) => answersMap[id]
  return showQuestionCore(question, getAnswer)
}

/**
 * Pure version of hideDueICHRA - wrapper around hideDueICHRACore for answersMap usage.
 * @param {string} key - The section key (e.g., 'ICHRA')
 * @param {Object} answersMap - Map of questionId -> answer value
 * @returns {boolean} - True if section should be hidden
 */
export function hideDueICHRAPure(key, answersMap) {
  const getAnswer = (id) => answersMap[id]
  return hideDueICHRACore(key, getAnswer)
}

/**
 * Pure version of isAssessmentComplete - wrapper around isAssessmentCompleteCore for answersMap usage.
 * @param {Object} assessmentQuestions - Assessment questions grouped by section
 * @param {Object} answersMap - Map of questionId -> answer value
 * @returns {boolean} - True if assessment is complete
 */
export function isAssessmentCompletePure(assessmentQuestions, answersMap) {
  const getAnswer = (id) => answersMap[id]
  return isAssessmentCompleteCore(assessmentQuestions, getAnswer)
}

/**
 * Pure version of isPlanComplete - wrapper around isPlanCompleteCore.
 * @param {Object} planQuestions - Plan questions grouped by section
 * @returns {boolean} - True if plan is complete
 */
export function isPlanCompletePure(planQuestions) {
  return isPlanCompleteCore(planQuestions)
}

/**
 * ScoreCalculationsService - Calculates compliance scores.
 * Supports both store-based usage (backward compatible) and context-based usage (store-independent).
 * 
 * @param {Object} context - Optional context for store-independent usage
 * @param {boolean} context.skipStores - If true, skips store initialization (required for context mode)
 * @param {Object} context.assessmentQuestions - Assessment questions grouped by section
 * @param {Object} context.answersMap - Map of questionId -> answer value
 * @param {Object} context.employer - Employer object with id, planYearId, etc.
 * @param {string} context.functionsBaseUrl - Base URL for API calls
 * @returns {Object} - { calculateSectionScore, calculateOverallScore, saveScore }
 */
export const ScoreCalculationsService = (context = {}) => {
  // Determine if we're using stores or context
  const useStores = !context.skipStores
  
  // Initialize stores only if needed (backward compatibility)
  const questionStore = useStores ? useQuestionsStore() : null
  const employerStore = useStores ? useEmployerStore() : null
  
  // Resolve baseUrl once at initialization
  const baseUrl = context.functionsBaseUrl || (useStores ? inject('functionsBaseUrl') : null)

  // Store refs (only if using stores)
  const storeAssessmentQuestions = useStores ? storeToRefs(questionStore).assessmentQuestions : null
  const storeActualEmployer = useStores ? storeToRefs(employerStore).actualEmployer : null
  
  // Getters that resolve from context or stores
  const getAssessmentQuestions = () => 
    context.assessmentQuestions || storeAssessmentQuestions?.value
  
  const getAnswerById = (id) => {
    if (context.answersMap) {
      return context.answersMap[id]
    }
    return questionStore?.getAnswerById(id)
  }
  
  const checkShowQuestion = (question) => {
    if (context.answersMap) {
      return showQuestionPure(question, context.answersMap)
    }
    return questionStore?.showQuestion(question)
  }
  
  const checkHideDueICHRA = (key) => {
    if (context.answersMap) {
      return hideDueICHRAPure(key, context.answersMap)
    }
    return questionStore?.hideDueICHRA(key)
  }
  
  const checkIsAssessmentComplete = () => {
    if (context.assessmentQuestions && context.answersMap) {
      return isAssessmentCompletePure(context.assessmentQuestions, context.answersMap)
    }
    return questionStore?.isAssessmentComplete()
  }
  
  const checkIsPlanComplete = () => {
    if (context.planQuestions) {
      return isPlanCompletePure(context.planQuestions)
    }
    return questionStore?.isPlanComplete()
  }
  
  const getEmployer = () => context.employer || storeActualEmployer?.value

  function calculateSectionScore() {
    const scores = []
    const assessmentQuestionsData = getAssessmentQuestions()
    
    if (!assessmentQuestionsData) {
      return scores
    }
    
    Object.values(assessmentQuestionsData).forEach((section) => {
      if (!section || section.length === 0) {
        return
      }
      
      let wrongAnswers = []
      let sectionShort = section[0].short_title
      let sectionWeight = section[0].section_weight
      let dollarRisk = section[0].dollar_risk

      if (!checkHideDueICHRA(sectionShort)) {
        //first we filter out the non visible questions
        let visibleQuestions = section.filter((val) => {
          if (val.hideLogic !== null) {
            return checkShowQuestion(val).show
          }
          return true
        })

        //And find the wrong answers
        wrongAnswers = visibleQuestions.filter((val) => {
          if (val.wrongAnswer !== null) {
            if (val.answer !== null) {
              let storedAnswer = val.answer
              let includes = false
              let wrongOptions = get(val, 'wrongAnswer.values', [])
              if (storedAnswer instanceof Array) {
                includes = storedAnswer.some((r) => wrongOptions.includes(r))
              } else {
                includes = wrongOptions.includes(storedAnswer)
              }
              if (includes) {
                return val
              }
            }
          }
        })

        //now we make the calculations
        const average =
          visibleQuestions.reduce((a, b) => a + parseFloat(b.score), 0) / visibleQuestions.length
        //const percFactorPerQuestion = 1 / sectionQuestions.length
        const questionWeights = visibleQuestions.map((question) => {
          let scoreContribution = 0
          if (question.answer !== null && question.answer !== undefined && question.answer !== '') {
            let includeQuestion = false
            if (question.answer instanceof Array && question.answer.length > 0) {
              includeQuestion = true
            } else if (!(question.answer instanceof Array) && !isNaN(question.answer)) {
              includeQuestion = true
            }
            if (includeQuestion) {
              let questionWeight = question.score / average
              let questionScore = 0.01 * (questionWeight * (100 / visibleQuestions.length))
              scoreContribution = questionScore * 10
            }
          }
          return [question.id, scoreContribution]
        })
        //now we filter the wrong answers from the question weights using the wrongAnswers array
        let sectionScore = questionWeights.reduce((a, b) => {
          if (wrongAnswers.length > 0) {
            if (wrongAnswers.some((val) => val.id === b[0])) {
              return a + 0
            } else {
              return a + parseFloat(b[1])
            }
          } else {
            return a + parseFloat(b[1])
          }
        }, 0)
        //Finally we calculate the dollar risk using the json object inside the sections and the response to teu questions
        let monRisk = calculateMonetaryRisk(dollarRisk)
        scores.push({
          name: sectionShort,
          weight: sectionWeight,
          score: (sectionScore * 10).toFixed(2), //Math.round(sectionScore * 10),
          color:
            sectionScore >= 7.5 ? 'success'
            : sectionScore >= 5.0 ? 'warning'
            : 'danger',
          dollarRisk: monRisk - ((monRisk * sectionScore) / 10).toFixed(2)
        })
      }
    })
    return scores
  }

  const calculateMonetaryRisk = (riskJson) => {
    //we load the answer for nrOfEmployees and insuranceType
    let currentInsurance
    let insuranceType = getAnswerById(11)
    let noEmployees = getAnswerById(9)
    //first we check if either of the answers is null
    if (
      noEmployees === null ||
      insuranceType === null ||
      noEmployees === undefined ||
      insuranceType === undefined
    ) {
      return 0
    }
    //now we check the insurance type
    switch (insuranceType) {
      case 0:
        currentInsurance = 'FI' // Fully insured
        break
      case 1:
        currentInsurance = 'SI' // Self insured
        break
      case 2:
        currentInsurance = 'LF' // Level funded
        break
      case 3:
        currentInsurance = 'DK' // Don't know
        break
    }
    //no we go over the risk matrix and calculate the risk
    let returnValue = null
    if (riskJson) {
      riskJson.forEach((risk) => {
        if (risk.insuranceType.includes(currentInsurance) && returnValue === null) {
          let noEmployeesArray = risk.employees.split(':')
          if (noEmployeesArray.length === 2) {
            let relation = noEmployeesArray[0]
            if (relation === 'under' && noEmployees < parseInt(noEmployeesArray[1])) {
              returnValue = risk.value
            } else if (relation === 'over' && noEmployees > parseInt(noEmployeesArray[1])) {
              returnValue = risk.value
            }
          } else {
            returnValue = risk.value
          }
        }
      })
    }
    //here we calculate
    /*SI/LF/DK/FI */
    return returnValue
  }

  function calculateOverallScore(scores) {
    //The below values are to validate here that the employer has the ichra
    let ichraQuestion = getAnswerById(12)
    if (ichraQuestion !== undefined) {
      if (typeof ichraQuestion === 'object' && ichraQuestion.value !== undefined) {
        ichraQuestion = JSON.parse(ichraQuestion.value)
      }
      let includesIchra = ichraQuestion.includes(10)
      let sectionsAvg = scores.reduce((acc, val) => {
        if (!includesIchra && val.name === 'ICHRA') {
          return acc
        } else {
          return acc + parseFloat(val.weight)
        }
      }, 0)
      sectionsAvg = sectionsAvg / (includesIchra ? scores.length : scores.length - 1)

      let weightedAvgScore = scores.reduce((acc, val) => {
        if (!includesIchra && val.name === 'ICHRA') {
          return acc
        } else {
          let weight = parseFloat(val.weight)
          let weighRelative = weight / sectionsAvg
          let weightedAvgScore = weighRelative * val.score
          return acc + weightedAvgScore
        }
      }, 0)
      let finalScore = weightedAvgScore / (includesIchra ? scores.length : scores.length - 1)
      return finalScore.toFixed(2)
    } else {
      return 0
    }
  }

  /**
   * Saves the calculated scores to the database.
   * Returns the calculated values so callers don't need to recalculate.
   * @returns {Promise<Object>} - { scores, totalComplianceRisk, percentageComplianceRisk, isAssessmentComplete, isPlanComplete }
   */
  async function saveScore() {
    const employer = getEmployer()
    
    if (!employer) {
      handleErrors('saveScore', new Error('No employer found'))
      return null
    }
    
    if (!baseUrl) {
      handleErrors('saveScore', new Error('No functionsBaseUrl found'))
      return null
    }
    
    const scores = calculateSectionScore()
    const totalComplianceRisk = scores.reduce((acc, val) => {
      return acc + val.dollarRisk
    }, 0)
    const percentageComplianceRisk = calculateOverallScore(scores)
    const assessmentComplete = checkIsAssessmentComplete()
    const planComplete = checkIsPlanComplete()

    if (parseFloat(percentageComplianceRisk) < 100 && totalComplianceRisk === 0) {
      Sentry.withScope((scope) => {
        scope.setLevel('warning')
        scope.setExtra('score_details', {
          employerId: employer.id,
          percentageComplianceRisk,
          totalComplianceRisk,
          sectionScores: scores.map((s) => ({
            name: s.name,
            score: s.score,
            dollarRisk: s.dollarRisk,
            weight: s.weight
          }))
        })
        Sentry.captureException(
          new Error(
            `Score inconsistency: percentage_compliance_risk is ${percentageComplianceRisk}% but total_compliance_risk is $0 for employer ${employer.id}`
          )
        )
      })
    }
    
    // Update employer object if using stores (backward compatibility)
    if (useStores && storeActualEmployer?.value) {
      storeActualEmployer.value['total_compliance_risk'] = totalComplianceRisk
      storeActualEmployer.value['percentage_compliance_risk'] = percentageComplianceRisk
      storeActualEmployer.value['is_assessment_complete'] = assessmentComplete
      storeActualEmployer.value['is_plan_complete'] = planComplete
    }
    
    // Prepare payload for API
    const payload = {
      id: employer.id,
      total_compliance_risk: totalComplianceRisk,
      percentage_compliance_risk: percentageComplianceRisk,
      is_assessment_complete: assessmentComplete,
      is_plan_complete: planComplete,
      complianceYearId: employer.planYearId || context.planYearId
    }
    
    await put(
      'updateEmployer',
      `${baseUrl}/employerGateway/api/v1/employers/${employer.id}`,
      payload
    )
    
    // Return calculated values so caller doesn't need to recalculate
    return {
      scores,
      totalComplianceRisk,
      percentageComplianceRisk,
      isAssessmentComplete: assessmentComplete,
      isPlanComplete: planComplete
    }
  }

  /**
   * Calculates scores without saving to the database (dry-run).
   * Useful for projecting what scores would be with a hypothetical answer change.
   * @returns {Object} - { scores, totalComplianceRisk, percentageComplianceRisk }
   */
  function calculateScore() {
    const scores = calculateSectionScore()
    const totalComplianceRisk = scores.reduce((acc, val) => {
      return acc + val.dollarRisk
    }, 0)
    const percentageComplianceRisk = calculateOverallScore(scores)

    return {
      scores,
      totalComplianceRisk,
      percentageComplianceRisk
    }
  }

  return {
    calculateSectionScore,
    calculateOverallScore,
    calculateMonetaryRisk,
    calculateScore,
    saveScore
  }
}
